import React, { useContext } from 'react';
import styled from 'styled-components';
// import background from './featuredAssetBackground.png';
import currencyFormatter from 'src/utils/currencyFormatter';
import DataState from 'src/components/DataState';
import * as Colors from 'src/styles/colors';
import { Link } from 'react-router-dom';
import AssetImage from 'src/components/shared/AssetImage';
import CarouselDots from 'src/components/shared/CarouselDots';
import SkeletonBlock from 'src/elements/Skeleton';
import { FEATURED_ASPECT_PERCENT } from 'src/config/imageConfig';
import moment from 'moment';
import { Text, View } from 'react-native';
import ResourcesContext from 'src/contexts/ResourcesContext';
import useWindowWidth from 'src/hooks/useWindowWidth';
import useCarouselIndex from 'src/hooks/useCarouselIndex';
import {
  PHONE_MAX_WIDTH, TABLET_MIN_WIDTH, MOBILE_MENU_MAX_WIDTH
} from 'src/styles/breakpoints';

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  color: #fff;
  padding: 5% 5% 15px;
  background-repeat: no-repeat;

  @media (min-width: 768px) {
    /* Fixed top padding so 'Featured Products' starts level with the
       About heading (HeroTextContainer margin-top) at any width. */
    padding-top: 25px;
  }

  @media (max-width: 768px) {
    background-color: ${Colors.elRed};
    margin-top: 15px;
    padding-bottom: 0;
  }

  @media only screen and (min-device-width: 481px) and (max-device-width: 1180px) and (orientation:portrait) {
    /* For portrait layouts only */
    font-size: 14px;
    background-color: ${Colors.elRed};

  }

`;

const HeaderLabel = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #fff;

  @media (min-width: 1030px) {
    font-size: 30px;
  }
`;

const ColumnContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-top: 20px;

  @media(max-width: 768px) {
    margin-bottom: 20px;
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  @media only screen and (min-device-width: 481px) and (max-device-width: 1180px) and (orientation:portrait) {
    /* For portrait layouts only */
    font-size: 14px;
    flex: 0.5;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: center;

  @media only screen and (min-device-width: 481px) and (max-device-width: 1180px) and (orientation:portrait) {
    /* For portrait layouts only */
    font-size: 14px;
    flex: 0.5;
  }
`;

/* Fixed-height, centered title area so rotating names never move the
   layout; the font size scales per name to stay within two lines. */
const AssetNameArea = styled.div`
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AssetName = styled.div`
  font-weight: bold;
  text-align: center;
  overflow-wrap: anywhere;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SalePrice = styled.div`
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  background-color: #000;
  color: #fff;
  font-weight: bold;
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
  align-self: center;

  @media (min-width: 768px) {
    width: 50%;
  }

  @media only screen and (min-device-width: 481px) and (max-device-width: 1180px) and (orientation:portrait) {
    /* For portrait layouts only */
    font-size: 12px;
    width: 90%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 0px;
  margin-bottom: 40px;
  gap: 12px;
  font-weight: bold;

  @media (min-width: 768px) {
    margin-top: auto;
    margin-bottom: 0px;
    padding-right: 20px;
  }
`;

const featuredButtonStyles = `
  box-sizing: border-box;
  width: 100%;
  min-height: 62px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 10px;
  padding: 8px 0;
`;

const DetailsButton = styled(Link)`
  ${featuredButtonStyles}
  color: #fff;
  border: 1px solid #fff;
`;

const BuyButton = styled(Link)`
  ${featuredButtonStyles}
  background-color: #fff;
  color: ${Colors.elRed};
`;

const placeholderButtonRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '30px',
  paddingRight: '20px',
};

export const FeaturedAssetPlaceholder = () => (
  <Container>
    <HeaderLabel>Featured Products</HeaderLabel>
    <ColumnContainer>
      <LeftColumn>
        <SkeletonBlock $inverse $width='80%' $height='25px' />
        <SkeletonBlock
          $inverse
          $width='55%'
          $height='25px'
          style={{ marginTop: '12px' }}
        />
        <div style={placeholderButtonRowStyle}>
          <SkeletonBlock $inverse $height='62px' $radius='10px' />
          <SkeletonBlock $inverse $height='62px' $radius='10px' />
        </div>
      </LeftColumn>
      <RightColumn>
        <div
          style={{
            width: '100%',
            paddingBottom: `${FEATURED_ASPECT_PERCENT}%`,
            position: 'relative',
          }}
        >
          <SkeletonBlock
            $inverse
            $radius='20px'
            style={{
              position: 'absolute',
              top: 0, right: 0, bottom: 0, left: 0,
              height: '100%',
            }}
          />
        </div>
      </RightColumn>
    </ColumnContainer>
  </Container>
);

const FeaturedAsset = ({assets}) => {
  const windowWidth = useWindowWidth();
  const smallScreen = windowWidth < TABLET_MIN_WIDTH;
  const mediumScreen =
    windowWidth > PHONE_MAX_WIDTH && windowWidth < MOBILE_MENU_MAX_WIDTH;
  const { clearAssets } = useContext(ResourcesContext);
  const [currentIndex, setCurrentIndex] = useCarouselIndex(assets?.length || 0);

  let asset = assets[currentIndex];

  if(!asset) return null

  const slug = asset => (
    asset.name.replace(/[^0-9a-z]+/gi, '-')
  );

  // Approximate two-line fit: available column width vs bold glyph width.
  const titleAreaWidth = smallScreen || mediumScreen ?
    windowWidth * 0.85 : windowWidth * 0.19;
  const titleFontSize = Math.max(14, Math.min(25,
    Math.floor((2 * titleAreaWidth) / (0.62 * (asset?.name?.length || 1)))
  ));

  return (
    <Container>
      <HeaderLabel>Featured Products</HeaderLabel>
      <ColumnContainer>
        <LeftColumn>
          <AssetNameArea>
            <AssetName style={{ fontSize: `${titleFontSize}px` }}>
              {asset?.name}
            </AssetName>
          </AssetNameArea>
          {!smallScreen & !mediumScreen ?
            <>
              {asset?.showListPrice && asset.listPrice && asset.listPrice > 0 &&
                <SalePrice>
                  {asset?.listPrice && asset?.listPrice > 0 ?
                    `SALE PRICE: ${currencyFormatter(asset?.listPrice)}` :
                    null
                  }
                  <div style={{marginTop: '10px'}}>
                    {`CALL (678) 788-0965`}
                  </div>
                </SalePrice>
              }
              {getButtons()}
            </> : null
          }
        </LeftColumn>
        <RightColumn>
          {getImage()}
        </RightColumn>
      </ColumnContainer>
      {smallScreen || mediumScreen ?
        <>
          {asset?.showListPrice && asset.listPrice && asset.listPrice > 0 &&
            <SalePrice>
              {asset?.listPrice && asset?.listPrice > 0 ?
                `SALE PRICE: ${currencyFormatter(asset?.listPrice)}` :
                null
              }
              <div style={{marginTop: '10px'}}>
                {`CALL (678) 788-0965`}
              </div>
            </SalePrice>
          }
          {getButtons()}
        </> :
        null
      }
      {assets.length > 1 &&
        <CarouselDots
          count={assets.length}
          currentIndex={currentIndex}
          onSelect={setCurrentIndex}
          style={{marginTop: '25px'}}
        />
      }
    </Container>
  )

  function getButtons() {
    return (
      <ButtonContainer>
        <DetailsButton
          to={asset.activeSaleAsset ?
            `/sales/${asset.activeSaleAsset.sale.id}/inventory/${asset.id}/${slug(asset)}` :
            `/inventory/${asset.id}/${slug(asset)}`
          }
          style={{textDecoration: 'none', cursor: 'pointer'}}
          onClick={() => clearAssets()}
        >
          View Details
        </DetailsButton>
        {getBuyButton()}
      </ButtonContainer>
    )
  }

  function getOptions() {
    let to = '';
    let label = '';
    let style = {textDecoration: 'none', cursor: 'pointer'}
    if(asset?.showListPrice) {
      to = `/contact/assetId=${asset.id}`;
      label = 'Buy Now';
    }else if(asset?.correctedLotNumber) {
      let bidding = asset.activeSaleAsset?.bidding;
      to = `/${bidding ? 'bidding' : 'sales'}/${asset.activeSaleAsset.sale.id}`;
      label = [
        'View Auction',
        `Date: ${moment(asset?.activeSaleAsset?.sale.saleOn).format('MM-DD-Y')}`
      ];
      style = {...style, fontSize: smallScreen || mediumScreen ? '12px' : '18px'}
    }else {
      to = '';
      label = 'Coming to a Future Auction';
      style = {textDecoration: 'none', cursor: 'auto'}
    }

    return {to: to, label: label, style: style}
  }

  function getImage() {
    let options = getOptions();
    return (
      <Link
        to={`/inventory/${asset?.id}/${slug(asset)}/${asset.showListPrice}`}
        style={{ display: 'block', width: '100%' }}
      >
        <AssetImage
          src={asset?.primaryPhotoUrl}
          alt={asset?.name}
          ratioPercent={FEATURED_ASPECT_PERCENT}
          radius="20px"
          showLoader={false}
        />
      </Link>
    )
  }

  function getBuyButton() {
    let options = getOptions();
    return (
      <BuyButton
        to={options.to}
        style={options.style}
      >
        {Array.isArray(options.label) ?
          options.label.map(string => {
            return (
              <View key={`${string}-label`}>
                <Text style={{fontWeight: 'bold', textAlign: 'center'}}>
                  {string}
                </Text>
              </View>
            )
          }) :
          <View key={`${options.label}-label`}>
            <Text style={{fontWeight: 'bold', textAlign: 'center'}}>
              {options.label}
            </Text>
          </View>
        }
      </BuyButton>
    )
  }
}

export default FeaturedAsset;
